import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";

const EFFECTIVE_DATE = "March 1, 2026";

const SECTIONS = [
  {
    title: "1. Scope and Purpose",
    content: `This Data Processing Agreement ("DPA") supplements the Opero Terms of Service and applies to all processing of personal data by Opero on behalf of the Customer. Opero acts as a Data Processor, and the Customer acts as the Data Controller, as defined under applicable data protection legislation including Ghana's Data Protection Act, 2012 (Act 843), and, where the Customer is established in the UK or EEA, the UK GDPR and EU GDPR.`,
  },
  {
    title: "2. Data Processing Details",
    content: `Opero processes personal data as necessary to provide the Service. The categories of data processed include:\n\n- Account data: names, email addresses, job titles\n- Usage data: task content, project data, comments, and files uploaded by users\n- Technical data: IP addresses, browser types, access logs\n\nProcessing activities include storage, retrieval, organization, and transmission of data as required to operate the Service.`,
  },
  {
    title: "3. Obligations of the Processor",
    content: `Opero shall:\n\n- Process personal data only on documented instructions from the Customer\n- Ensure that persons authorized to process the data are bound by confidentiality obligations\n- Implement appropriate technical and organizational security measures\n- Assist the Customer in responding to data subject access requests\n- Delete or return all personal data upon termination of the agreement\n- Make available information necessary to demonstrate compliance\n- Submit to audits and inspections conducted by the Customer or an authorized auditor`,
  },
  {
    title: "4. Sub-Processors",
    content: `Opero may engage sub-processors to assist in providing the Service. A current list of sub-processors is available upon request. Opero will provide at least 30 days' notice before adding new sub-processors and will ensure each sub-processor is bound by data protection obligations consistent with this DPA.`,
  },
  {
    title: "5. International Transfers",
    content: `Where personal data is transferred outside the data subject's jurisdiction (including transfers from Ghana, the UK, or the EEA), Opero ensures appropriate safeguards are in place, including Standard Contractual Clauses (SCCs) as approved by the relevant supervisory authority and the Data Protection Commission of Ghana, supplemented by additional technical and organizational measures where required.`,
  },
  {
    title: "6. Security Measures",
    content: `Opero maintains security measures including:\n\n- Encryption of data in transit (TLS 1.3) and at rest (AES-256)\n- Regular vulnerability assessments and penetration testing\n- Access controls with role-based permissions and multi-factor authentication\n- Incident detection and response procedures\n- Business continuity and disaster recovery plans\n- Employee security training and background checks`,
  },
  {
    title: "7. Data Breach Notification",
    content: `In the event of a personal data breach, Opero will notify the Customer without undue delay and no later than 72 hours after becoming aware of the breach. The notification will include the nature of the breach, categories and approximate number of data subjects affected, likely consequences, and measures taken or proposed to address the breach.`,
  },
  {
    title: "8. Duration and Termination",
    content: `This DPA remains in effect for the duration of the Service agreement. Upon termination, Opero will delete all personal data within 90 days unless retention is required by applicable law.`,
  },
  {
    title: "9. Contact",
    content: `For questions about this DPA, contact our Data Protection team at corporate@digiits.com or write to: Digiits, Accra, Ghana.`,
  },
];

export function DpaPage() {
  return (
    <main className="pt-28 pb-20">
      <article className="mx-auto max-w-3xl px-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Home
        </Link>

        <h1 className="mt-8 text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
          Data Processing Agreement
        </h1>
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
          Effective date: {EFFECTIVE_DATE}
        </p>

        <div className="mt-6 border-b border-slate-200 dark:border-slate-800 pb-6 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
          This Data Processing Agreement outlines how Opero processes personal
          data on behalf of our customers in compliance with applicable data
          protection laws.
        </div>

        <div className="mt-8 space-y-10">
          {SECTIONS.map((section) => (
            <section key={section.title}>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                {section.title}
              </h2>
              <div className="mt-3 space-y-3">
                {section.content.split("\n\n").map((block, i) => {
                  const trimmed = block.trim();
                  if (trimmed.startsWith("- ")) {
                    const items = trimmed.split("\n").filter((l) => l.trim());
                    return (
                      <ul
                        key={i}
                        className="list-disc space-y-1.5 pl-6 text-sm leading-relaxed text-slate-600 dark:text-slate-300"
                      >
                        {items.map((item, j) => (
                          <li key={j}>{item.replace(/^-\s*/, "")}</li>
                        ))}
                      </ul>
                    );
                  }
                  return (
                    <p
                      key={i}
                      className="text-sm leading-relaxed text-slate-600 dark:text-slate-300"
                    >
                      {trimmed}
                    </p>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 border-t border-slate-200 dark:border-slate-800 pt-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary-500 dark:text-primary-400 hover:underline"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </div>
      </article>
    </main>
  );
}
